import { EyeOff, X } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { cn } from "./ui";

let keys: string[] | null = null;
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());
const fetchDismissed = async () => { if (keys === null) { keys = await api.get("/dashboard/dismissed").then((r) => r.data.keys as string[]).catch(() => []); notify(); } return keys; };

export const NOTICE_LABELS: Record<string, string> = { "rollover-notice": "Rollover awal bulan", "device-alert-notice": "HP siswa berhenti menerima notifikasi", "doc-return-notice": "Pengembalian dokumen", "incomplete-notice": "Data siswa belum lengkap" };

export function useDismissedKeys() {
  const [k, setK] = useState<string[] | null>(keys);
  useEffect(() => { const f = () => setK(keys ? [...keys] : null); subs.add(f); fetchDismissed(); return () => { subs.delete(f); }; }, []);
  const undismiss = async (key: string) => {
    try { await api.post("/dashboard/undismiss", { key }); keys = (keys || []).filter((x) => x !== key); notify(); toast.success("Notifikasi ditampilkan kembali"); } catch (e) { toast.error(errMsg(e)); }
  };
  return { keys: k, undismiss };
}

export function useDismissed(key: string) {
  const { keys: k } = useDismissedKeys();
  const hidden = k === null ? null : k.includes(key);
  const dismiss = async () => {
    try { await api.post("/dashboard/dismiss", { key }); keys = [...(keys || []), key]; notify(); toast.success("Notifikasi disembunyikan sampai besok"); } catch (e) { toast.error(errMsg(e)); }
  };
  return { hidden, dismiss };
}

export function HiddenNotices() {
  const { keys: k, undismiss } = useDismissedKeys();
  if (!k?.length) return null;
  return (
    <div className="rounded-xl border border-dashed bg-card px-4 py-2.5 text-xs flex flex-wrap items-center gap-2" data-testid="hidden-notices">
      <span className="inline-flex items-center gap-1.5 text-muted-foreground font-semibold"><EyeOff className="h-3.5 w-3.5" /> {k.length} notifikasi disembunyikan hari ini:</span>
      {k.map((key) => (
        <button key={key} type="button" data-testid={`hidden-notice-show-${key}`} onClick={() => undismiss(key)} className="inline-flex items-center gap-1 rounded-full border bg-muted/40 hover:bg-muted px-2.5 py-0.5 font-medium transition-colors">
          {NOTICE_LABELS[key] || key} <span className="text-primary">· Tampilkan</span>
        </button>
      ))}
    </div>
  );
}

type Props = { id: string; tone: "amber" | "sky" | "red" | "orange" | "emerald"; icon: ReactNode; title: ReactNode; to?: string; action?: ReactNode; children?: ReactNode };
const tones = {
  emerald: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-100",
  amber: "border-amber-400 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 [&_.nt]:text-amber-800 dark:[&_.nt]:text-amber-200",
  sky: "border-sky-400 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-800 [&_.nt]:text-sky-800 dark:[&_.nt]:text-sky-200",
  red: "border-red-400 bg-red-50 dark:bg-red-950/30 dark:border-red-800 [&_.nt]:text-red-800 dark:[&_.nt]:text-red-200",
  orange: "border-orange-400 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-800 [&_.nt]:text-orange-800 dark:[&_.nt]:text-orange-200",
};

export function NoticeShell({ id, tone, icon, title, to, action, children }: Props) {
  const nav = useNavigate();
  const { hidden, dismiss } = useDismissed(id);
  if (hidden !== false) return null;
  return (
    <div className={cn("rise rounded-lg border p-3 relative", tones[tone], to && "cursor-pointer hover:shadow-md transition-shadow")} data-testid={id} onClick={() => to && nav(to)} role={to ? "link" : undefined}>
      <button type="button" data-testid={`${id}-dismiss`} title="Sembunyikan sampai besok" onClick={(e) => { e.stopPropagation(); dismiss(); }} className="absolute top-2 right-2 rounded-md p-1 text-muted-foreground hover:bg-black/5 dark:hover:bg-white/10 hover:text-foreground"><X className="h-4 w-4" /></button>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 pr-7">
        <div className="nt flex items-center gap-2 font-bold">{icon}{title}</div>
        {action && <div className="sm:ml-auto text-sm" onClick={(e) => e.stopPropagation()}>{action}</div>}
      </div>
      {children}
    </div>
  );
}
